import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  VStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  useToast,
  FormErrorMessage,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  HStack,
  Icon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormHelperText,
  SimpleGrid,
  Badge,
  Divider,
  Card,
  CardBody,
  CardHeader,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Select,
} from '@chakra-ui/react'
import {
  FiArrowLeft,
  FiUsers,
  FiUser,
  FiFile,
  FiFileText,
  FiTag,
  FiMessageSquare,
  FiCreditCard,
  FiHome,
  FiCheck,
  FiHardDrive,
  FiEdit,
} from 'react-icons/fi'
import { useAdminGuard } from '@/hooks/useAdminGuard'
import { usePlans } from '@/hooks/usePlans'
import { Organization } from '@/types'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

function PlanFeature({ icon, children }: { icon: React.ElementType; children: React.ReactNode }) {
  return (
    <HStack spacing={2} align="center">
      <Icon as={icon} boxSize={3.5} color="blue.400" flexShrink={0} />
      <Text fontSize="sm" color="gray.600">
        {children}
      </Text>
    </HStack>
  )
}

export default function CreateOrganizationPage() {
  const { isAuthorized, ForbiddenView } = useAdminGuard()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const toast = useToast()
  const isEditMode = !!id
  const { t } = useAppTranslation('admin')

  const { plans: availablePlans, loading: plansLoading, error: plansError } = usePlans()

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    language: '',
    access_expires_at: '',
  })
  const [selectedPlanId, setSelectedPlanId] = useState<string>('')
  const [planMode, setPlanMode] = useState<'preset' | 'custom'>('preset')
  const [customLimits, setCustomLimits] = useState({
    file_limit: 1000,
    page_limit: 5000,
    users_limit: 5,
    smart_tagging_limit: 100,
    smart_renaming_limit: 100,
    chatbot_queries_limit: 500,
    storage_limit_gb: 10,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [pageLoading, setPageLoading] = useState(isEditMode)

  useEffect(() => {
    if (!isEditMode && availablePlans.length > 0 && !selectedPlanId) {
      setSelectedPlanId(availablePlans[0].id.toString())
    }
  }, [availablePlans, selectedPlanId, isEditMode])

  useEffect(() => {
    if (isEditMode) {
      loadOrganization()
    }
  }, [isEditMode, id])

  const loadOrganization = async () => {
    try {
      setPageLoading(true)
      const orgData = await apiService.getAdminOrganization(parseInt(id!))
      setOrganization(orgData)

      setFormData((prev) => ({
        ...prev,
        name: orgData.name,
        description: orgData.description || '',
        language: orgData.language ?? orgData.settings?.language ?? '',
        access_expires_at: orgData.access_expires_at ?? '',
      }))

      if (orgData.plan) {
        const plan = orgData.plan
        if (plan.type === 'custom' && plan.limits) {
          setPlanMode('custom')
          setCustomLimits({
            file_limit: plan.limits.file_limit || 1000,
            page_limit: plan.limits.page_limit || 5000,
            users_limit: plan.limits.users_limit || 5,
            smart_tagging_limit: plan.limits.smart_tagging_limit || 100,
            smart_renaming_limit: plan.limits.smart_renaming_limit || 100,
            chatbot_queries_limit: plan.limits.chatbot_queries_limit || 500,
            storage_limit_gb: plan.limits.storage_limit_gb || 10,
          })
        } else {
          setPlanMode('preset')
          setSelectedPlanId(plan.id.toString())
        }
      }
    } catch (error: any) {
      toast({
        title: t('createOrganization.toast.loadError'),
        description: error.response?.data?.message || t('createOrganization.toast.loadErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      navigate('/admin/organizations')
    } finally {
      setPageLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string | number) => {
    setFormData({ ...formData, [field]: value })
    if (errors[field]) setErrors({ ...errors, [field]: '' })
  }

  const handleCustomLimitChange = (field: string, value: number) => {
    setCustomLimits({ ...customLimits, [field]: value })
    if (errors[field]) setErrors({ ...errors, [field]: '' })
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) newErrors.name = t('createOrganization.validation.nameRequired')

    if (!isEditMode) {
      if (!formData.admin_name.trim())
        newErrors.admin_name = t('createOrganization.validation.adminNameRequired')
      if (!formData.admin_email.trim()) {
        newErrors.admin_email = t('createOrganization.validation.adminEmailRequired')
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.admin_email)) {
        newErrors.admin_email = t('createOrganization.validation.invalidEmail')
      }
      if (!formData.admin_password) {
        newErrors.admin_password = t('createOrganization.validation.passwordRequired')
      } else if (formData.admin_password.length < 8) {
        newErrors.admin_password = t('createOrganization.validation.passwordMinLength')
      }
    }

    if (planMode === 'preset' && !selectedPlanId)
      newErrors.plan = t('createOrganization.plan.selectPlan')

    if (planMode === 'custom') {
      if (customLimits.file_limit <= 0)
        newErrors.file_limit = t('createOrganization.validation.fileLimitRequired')
      if (customLimits.page_limit <= 0)
        newErrors.page_limit = t('createOrganization.validation.pageLimitRequired')
      if (customLimits.users_limit <= 0)
        newErrors.users_limit = t('createOrganization.validation.usersLimitRequired')
      if (customLimits.smart_tagging_limit <= 0)
        newErrors.smart_tagging_limit = t('createOrganization.validation.smartTaggingLimitRequired')
      if (customLimits.smart_renaming_limit <= 0)
        newErrors.smart_renaming_limit = t('createOrganization.validation.smartRenamingLimitRequired')
      if (customLimits.chatbot_queries_limit <= 0)
        newErrors.chatbot_queries_limit = t('createOrganization.validation.chatbotQueriesLimitRequired')
      if (customLimits.storage_limit_gb <= 0)
        newErrors.storage_limit_gb = t('createOrganization.validation.storageLimitRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    try {
      setLoading(true)

      if (isEditMode) {
        if (!organization) throw new Error('Organization data not loaded')

        const updateData = {
          name: formData.name,
          description: formData.description || '',
          language: (formData.language || null) as 'en' | 'es' | null,
          access_expires_at: formData.access_expires_at || null,
        }
        const updatedOrg = await apiService.updateAdminOrganization(parseInt(id!), updateData)

        if (
          planMode === 'preset' &&
          selectedPlanId &&
          organization?.plan?.id.toString() !== selectedPlanId
        ) {
          await apiService.assignPlanToOrganization(
            parseInt(selectedPlanId),
            parseInt(id!),
            'Plan updated via admin'
          )
        } else if (planMode === 'custom') {
          const currentLimits = organization?.plan?.limits
          const hasChangedLimits =
            !currentLimits ||
            currentLimits.file_limit !== customLimits.file_limit ||
            currentLimits.smart_tagging_limit !== customLimits.smart_tagging_limit ||
            currentLimits.smart_renaming_limit !== customLimits.smart_renaming_limit ||
            currentLimits.chatbot_queries_limit !== customLimits.chatbot_queries_limit ||
            currentLimits.storage_limit_gb !== customLimits.storage_limit_gb

          if (hasChangedLimits || organization?.plan?.type !== 'custom') {
            const customPlan = await apiService.createCustomPlan({
              name: `Custom Plan for ${formData.name}`,
              description: `Custom plan created for organization ${formData.name}`,
              file_limit: customLimits.file_limit,
              page_limit: customLimits.page_limit,
              users_limit: customLimits.users_limit,
              smart_tagging_limit: customLimits.smart_tagging_limit,
              smart_renaming_limit: customLimits.smart_renaming_limit,
              chatbot_queries_limit: customLimits.chatbot_queries_limit,
              storage_limit_gb: customLimits.storage_limit_gb,
              organization_id: parseInt(id!),
            })
            await apiService.assignPlanToOrganization(
              customPlan.plan.id,
              parseInt(id!),
              'Custom plan created and assigned'
            )
          }
        }

        toast({
          title: t('createOrganization.toast.updated'),
          description: t('createOrganization.toast.updatedDescription', { name: updatedOrg.name }),
          status: 'success',
          duration: 5000,
          isClosable: true,
        })

        navigate(`/admin/organizations/${id}`)
      } else {
        let organizationData: any = {
          name: formData.name,
          description: formData.description || undefined,
          admin_name: formData.admin_name,
          admin_email: formData.admin_email,
          admin_password: formData.admin_password,
        }

        if (formData.language) {
          organizationData.settings = { language: formData.language }
        }

        if (formData.access_expires_at) {
          organizationData.access_expires_at = formData.access_expires_at
        }

        if (planMode === 'preset' && selectedPlanId) {
          organizationData.plan_id = parseInt(selectedPlanId)
        } else if (planMode === 'custom') {
          const customPlan = await apiService.createCustomPlan({
            name: `Custom Plan for ${formData.name}`,
            description: `Custom plan created for organization ${formData.name}`,
            file_limit: customLimits.file_limit,
            page_limit: customLimits.page_limit,
            users_limit: customLimits.users_limit,
            smart_tagging_limit: customLimits.smart_tagging_limit,
            smart_renaming_limit: customLimits.smart_renaming_limit,
            chatbot_queries_limit: customLimits.chatbot_queries_limit,
            storage_limit_gb: customLimits.storage_limit_gb,
          })
          organizationData.plan_id = customPlan.plan.id
        }

        const org = await apiService.createAdminOrganization(organizationData)

        toast({
          title: t('createOrganization.toast.created'),
          description: t('createOrganization.toast.createdDescription', { name: org.name }),
          status: 'success',
          duration: 5000,
          isClosable: true,
        })

        navigate('/admin/organizations')
      }
    } catch (error: any) {
      toast({
        title: isEditMode
          ? t('createOrganization.toast.errorUpdating')
          : t('createOrganization.toast.errorCreating'),
        description:
          error.response?.data?.message ||
          (isEditMode
            ? t('createOrganization.toast.failedToUpdate')
            : t('createOrganization.toast.failedToCreate')),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => navigate('/admin/organizations')

  if (!isAuthorized) return ForbiddenView

  if (pageLoading) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <Text>{t('createOrganization.loadingDetails')}</Text>
      </Box>
    )
  }

  if (plansError) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          {t('createOrganization.failedToLoadPlans', { error: plansError })}
        </Alert>
      </Box>
    )
  }

  return (
    <Box p={{ base: 4, md: 6 }}>
      <VStack spacing={6} align="stretch">
        {/* Info Alert -- top of page */}
        <Alert status="info" borderRadius="lg" alignItems="flex-start">
          <AlertIcon mt={0.5} />
          <Box>
            <AlertTitle fontSize="sm">{t('createOrganization.alert.thingsToKnow')}</AlertTitle>
            <AlertDescription fontSize="sm">
              {t('createOrganization.alert.slugAutoGenerated')}
              {!isEditMode && t('createOrganization.alert.ownerAdminPrivileges')}
              {t('createOrganization.alert.aiLimitsReset')}
            </AlertDescription>
          </Box>
        </Alert>

        {/* Header */}
        <Box>
          <Button
            variant="ghost"
            leftIcon={<FiArrowLeft />}
            onClick={handleCancel}
            mb={4}
            size="sm"
          >
            {t('createOrganization.backToOrganizations')}
          </Button>
          <HStack spacing={3} mb={1}>
            <Icon as={FiUsers} boxSize={5} color="blue.500" />
            <Heading size="md">
              {isEditMode ? t('createOrganization.editTitle') : t('createOrganization.title')}
            </Heading>
          </HStack>
          <Text color="gray.500" fontSize="sm">
            {isEditMode
              ? t('createOrganization.editDescription')
              : t('createOrganization.description')}
          </Text>
        </Box>

        <form onSubmit={handleSubmit}>
          <VStack spacing={6} align="stretch">
            {/* Two-column section: Org Info + Owner Account */}
            <SimpleGrid
              columns={{ base: 1, lg: isEditMode ? 1 : 2 }}
              spacing={6}
              alignItems="stretch"
            >
              {/* Card: Organization Information */}
              <Card
                variant="outline"
                shadow="sm"
                borderLeft="3px solid"
                borderLeftColor="blue.400"
              >
                <CardHeader pb={2} bg="blue.50" borderTopRightRadius="md">
                  <HStack spacing={2}>
                    <Icon as={FiHome} color="blue.500" boxSize={4} />
                    <Heading size="sm">{t('createOrganization.orgInfo.sectionTitle')}</Heading>
                  </HStack>
                </CardHeader>
                <CardBody pt={4}>
                  <VStack spacing={4} align="stretch">
                    <FormControl isRequired isInvalid={!!errors.name}>
                      <FormLabel>{t('createOrganization.orgInfo.orgName')}</FormLabel>
                      <Input
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder={t('createOrganization.orgInfo.orgNamePlaceholder')}
                      />
                      <FormErrorMessage>{errors.name}</FormErrorMessage>
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t('createOrganization.orgInfo.description')}</FormLabel>
                      <Textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder={t('createOrganization.orgInfo.descriptionPlaceholder')}
                        rows={4}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t('createOrganization.orgInfo.language')}</FormLabel>
                      <Select
                        value={formData.language}
                        onChange={(e) => handleInputChange('language', e.target.value)}
                      >
                        <option value="">{t('createOrganization.orgInfo.languageNotSet')}</option>
                        <option value="en">English</option>
                        <option value="es">Español</option>
                      </Select>
                      <FormHelperText>
                        {t('createOrganization.orgInfo.languageHelper')}
                      </FormHelperText>
                    </FormControl>

                    <FormControl>
                      <FormLabel>{t('createOrganization.orgInfo.accessExpiresAt')}</FormLabel>
                      <Input
                        type="date"
                        value={formData.access_expires_at}
                        onChange={(e) => handleInputChange('access_expires_at', e.target.value)}
                      />
                      <FormHelperText>
                        {t('createOrganization.orgInfo.accessExpiresAtHelper')}
                      </FormHelperText>
                    </FormControl>
                  </VStack>
                </CardBody>
              </Card>

              {/* Card: Owner User Account (create mode only) */}
              {!isEditMode && (
                <Card
                  variant="outline"
                  shadow="sm"
                  borderLeft="3px solid"
                  borderLeftColor="green.400"
                >
                  <CardHeader pb={2} bg="green.50" borderTopRightRadius="md">
                    <HStack spacing={2}>
                      <Icon as={FiUser} color="green.500" boxSize={4} />
                      <Heading size="sm">
                        {t('createOrganization.ownerAccount.sectionTitle')}
                      </Heading>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {t('createOrganization.ownerAccount.subtitle')}
                    </Text>
                  </CardHeader>
                  <CardBody pt={4}>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired isInvalid={!!errors.admin_name}>
                        <FormLabel>{t('createOrganization.ownerAccount.fullName')}</FormLabel>
                        <Input
                          value={formData.admin_name}
                          onChange={(e) => handleInputChange('admin_name', e.target.value)}
                          placeholder={t('createOrganization.ownerAccount.namePlaceholder')}
                        />
                        <FormErrorMessage>{errors.admin_name}</FormErrorMessage>
                      </FormControl>

                      <FormControl isRequired isInvalid={!!errors.admin_email}>
                        <FormLabel>{t('createOrganization.ownerAccount.emailAddress')}</FormLabel>
                        <Input
                          type="email"
                          value={formData.admin_email}
                          onChange={(e) => handleInputChange('admin_email', e.target.value)}
                          placeholder={t('createOrganization.ownerAccount.emailPlaceholder')}
                        />
                        <FormErrorMessage>{errors.admin_email}</FormErrorMessage>
                      </FormControl>

                      <FormControl isRequired isInvalid={!!errors.admin_password}>
                        <FormLabel>{t('createOrganization.ownerAccount.password')}</FormLabel>
                        <Input
                          type="password"
                          value={formData.admin_password}
                          onChange={(e) => handleInputChange('admin_password', e.target.value)}
                          placeholder={t('createOrganization.ownerAccount.passwordPlaceholder')}
                        />
                        <FormHelperText>
                          {t('createOrganization.ownerAccount.passwordHelperText')}
                        </FormHelperText>
                        <FormErrorMessage>{errors.admin_password}</FormErrorMessage>
                      </FormControl>
                    </VStack>
                  </CardBody>
                </Card>
              )}
            </SimpleGrid>

            {/* Card: Plan Selection -- full width */}
            <Card variant="outline" shadow="sm" borderLeft="3px solid" borderLeftColor="purple.400">
              <CardHeader pb={2} bg="purple.50" borderTopRightRadius="md">
                <HStack spacing={2}>
                  <Icon as={FiCreditCard} color="purple.500" boxSize={4} />
                  <Heading size="sm">{t('createOrganization.plan.sectionTitle')}</Heading>
                </HStack>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  {t('createOrganization.plan.subtitle')}
                </Text>
              </CardHeader>
              <CardBody pt={4}>
                <Tabs
                  index={planMode === 'preset' ? 0 : 1}
                  onChange={(i) => setPlanMode(i === 0 ? 'preset' : 'custom')}
                  variant="soft-rounded"
                  colorScheme="blue"
                  size="sm"
                >
                  <TabList mb={5}>
                    <Tab>{t('createOrganization.plan.presetPlans')}</Tab>
                    <Tab>{t('createOrganization.plan.customLimits')}</Tab>
                  </TabList>

                  <TabPanels>
                    {/* Preset Plans */}
                    <TabPanel p={0}>
                      {plansLoading ? (
                        <Text color="gray.500" fontSize="sm">
                          {t('createOrganization.plan.loadingPlans')}
                        </Text>
                      ) : (
                        <>
                          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
                            {availablePlans.map((plan) => {
                              const isSelected = selectedPlanId === plan.id.toString()
                              const isRecommended = plan.slug === 'professional'
                              return (
                                <Box
                                  key={plan.id}
                                  onClick={() => setSelectedPlanId(plan.id.toString())}
                                  cursor="pointer"
                                  position="relative"
                                  border="2px solid"
                                  borderColor={isSelected ? 'blue.500' : 'gray.200'}
                                  bg={isSelected ? 'blue.50' : 'white'}
                                  borderRadius="xl"
                                  p={5}
                                  transition="all 0.2s"
                                  _hover={{
                                    borderColor: isSelected ? 'blue.500' : 'blue.300',
                                    shadow: 'lg',
                                    transform: 'translateY(-2px)',
                                  }}
                                  shadow={isSelected ? 'md' : 'sm'}
                                >
                                  {isRecommended && (
                                    <Badge
                                      position="absolute"
                                      top={-3}
                                      left="50%"
                                      transform="translateX(-50%)"
                                      colorScheme="purple"
                                      px={3}
                                      py={0.5}
                                      borderRadius="full"
                                      fontSize="xs"
                                      fontWeight="bold"
                                      textTransform="uppercase"
                                      letterSpacing="wide"
                                    >
                                      {t('createOrganization.plan.popular')}
                                    </Badge>
                                  )}

                                  <VStack align="start" spacing={4}>
                                    <Text fontWeight="bold" fontSize="lg">
                                      {plan.name}
                                    </Text>

                                    <Box>
                                      {!plan.price_monthly ? (
                                        <Text fontSize="3xl" fontWeight="bold" color="green.500">
                                          {t('createOrganization.plan.free')}
                                        </Text>
                                      ) : (
                                        <HStack align="baseline" spacing={1}>
                                          <Text fontSize="3xl" fontWeight="bold">
                                            ${plan.price_monthly}
                                          </Text>
                                          <Text fontSize="sm" color="gray.500">
                                            {t('createOrganization.plan.perMonth')}
                                          </Text>
                                        </HStack>
                                      )}
                                    </Box>

                                    {plan.description && (
                                      <Text fontSize="xs" color="gray.500" noOfLines={2}>
                                        {plan.description}
                                      </Text>
                                    )}

                                    <Divider />

                                    <VStack align="start" spacing={2} w="full">
                                      <PlanFeature icon={FiFile}>
                                        {t('createOrganization.plan.features.files', { value: plan.limits.file_limit.toLocaleString() })}
                                      </PlanFeature>
                                      <PlanFeature icon={FiFileText}>
                                        {t('createOrganization.plan.features.pages', { value: plan.limits.page_limit.toLocaleString() })}
                                      </PlanFeature>
                                      <PlanFeature icon={FiUsers}>
                                        {t('createOrganization.plan.features.users', { value: plan.limits.users_limit })}
                                      </PlanFeature>
                                      <PlanFeature icon={FiHardDrive}>
                                        {t('createOrganization.plan.features.storage', { value: plan.limits.storage_limit_gb })}
                                      </PlanFeature>
                                      <PlanFeature icon={FiTag}>
                                        {t('createOrganization.plan.features.smartTags', { value: plan.limits.smart_tagging_limit.toLocaleString() })}
                                      </PlanFeature>
                                      <PlanFeature icon={FiEdit}>
                                        {t('createOrganization.plan.features.smartRenames', { value: plan.limits.smart_renaming_limit.toLocaleString() })}
                                      </PlanFeature>
                                      <PlanFeature icon={FiMessageSquare}>
                                        {t('createOrganization.plan.features.chatQueries', { value: plan.limits.chatbot_queries_limit.toLocaleString() })}
                                      </PlanFeature>
                                    </VStack>

                                    {isSelected && (
                                      <HStack w="full" justify="center" spacing={1} pt={1}>
                                        <Icon as={FiCheck} color="blue.500" boxSize={4} />
                                        <Text
                                          fontSize="sm"
                                          fontWeight="semibold"
                                          color="blue.500"
                                        >
                                          {t('createOrganization.plan.selected')}
                                        </Text>
                                      </HStack>
                                    )}
                                  </VStack>
                                </Box>
                              )
                            })}
                          </SimpleGrid>
                          {errors.plan && (
                            <Text color="red.500" fontSize="sm" mt={3}>
                              {errors.plan}
                            </Text>
                          )}
                        </>
                      )}
                    </TabPanel>

                    {/* Custom Limits */}
                    <TabPanel p={0}>
                      <Alert
                        status="info"
                        borderRadius="lg"
                        mb={5}
                        variant="subtle"
                        fontSize="sm"
                      >
                        <AlertIcon />
                        {t('createOrganization.plan.customLimitsInfo')}
                      </Alert>

                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                        <FormControl isInvalid={!!errors.file_limit}>
                          <FormLabel fontSize="sm">
                            {t('createOrganization.plan.customFields.fileLimit')}
                          </FormLabel>
                          <NumberInput
                            value={customLimits.file_limit}
                            onChange={(_, v) => handleCustomLimitChange('file_limit', v || 0)}
                            min={1}
                            max={100000}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <FormHelperText fontSize="xs">
                            {t('createOrganization.plan.customFields.fileLimitHelper')}
                          </FormHelperText>
                          <FormErrorMessage>{errors.file_limit}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors.page_limit}>
                          <FormLabel fontSize="sm">
                            {t('createOrganization.plan.customFields.pageLimit')}
                          </FormLabel>
                          <NumberInput
                            value={customLimits.page_limit}
                            onChange={(_, v) => handleCustomLimitChange('page_limit', v || 0)}
                            min={1}
                            max={1000000}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <FormHelperText fontSize="xs">
                            {t('createOrganization.plan.customFields.pageLimitHelper')}
                          </FormHelperText>
                          <FormErrorMessage>{errors.page_limit}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors.users_limit}>
                          <FormLabel fontSize="sm">
                            {t('createOrganization.plan.customFields.usersLimit')}
                          </FormLabel>
                          <NumberInput
                            value={customLimits.users_limit}
                            onChange={(_, v) => handleCustomLimitChange('users_limit', v || 0)}
                            min={1}
                            max={10000}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <FormHelperText fontSize="xs">
                            {t('createOrganization.plan.customFields.usersLimitHelper')}
                          </FormHelperText>
                          <FormErrorMessage>{errors.users_limit}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors.storage_limit_gb}>
                          <FormLabel fontSize="sm">
                            {t('createOrganization.plan.customFields.storageGb')}
                          </FormLabel>
                          <NumberInput
                            value={customLimits.storage_limit_gb}
                            onChange={(_, v) =>
                              handleCustomLimitChange('storage_limit_gb', v || 0)
                            }
                            min={1}
                            max={10000}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <FormHelperText fontSize="xs">
                            {t('createOrganization.plan.customFields.storageGbHelper')}
                          </FormHelperText>
                          <FormErrorMessage>{errors.storage_limit_gb}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors.smart_tagging_limit}>
                          <FormLabel fontSize="sm">
                            {t('createOrganization.plan.customFields.smartTagging')}
                          </FormLabel>
                          <NumberInput
                            value={customLimits.smart_tagging_limit}
                            onChange={(_, v) =>
                              handleCustomLimitChange('smart_tagging_limit', v || 0)
                            }
                            min={1}
                            max={100000}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <FormHelperText fontSize="xs">
                            {t('createOrganization.plan.customFields.smartTaggingHelper')}
                          </FormHelperText>
                          <FormErrorMessage>{errors.smart_tagging_limit}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors.smart_renaming_limit}>
                          <FormLabel fontSize="sm">
                            {t('createOrganization.plan.customFields.smartRenaming')}
                          </FormLabel>
                          <NumberInput
                            value={customLimits.smart_renaming_limit}
                            onChange={(_, v) =>
                              handleCustomLimitChange('smart_renaming_limit', v || 0)
                            }
                            min={1}
                            max={100000}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <FormHelperText fontSize="xs">
                            {t('createOrganization.plan.customFields.smartRenamingHelper')}
                          </FormHelperText>
                          <FormErrorMessage>{errors.smart_renaming_limit}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={!!errors.chatbot_queries_limit}>
                          <FormLabel fontSize="sm">
                            {t('createOrganization.plan.customFields.chatQueries')}
                          </FormLabel>
                          <NumberInput
                            value={customLimits.chatbot_queries_limit}
                            onChange={(_, v) =>
                              handleCustomLimitChange('chatbot_queries_limit', v || 0)
                            }
                            min={1}
                            max={100000}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                          <FormHelperText fontSize="xs">
                            {t('createOrganization.plan.customFields.chatQueriesHelper')}
                          </FormHelperText>
                          <FormErrorMessage>{errors.chatbot_queries_limit}</FormErrorMessage>
                        </FormControl>
                      </SimpleGrid>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </CardBody>
            </Card>

            {/* Actions */}
            <HStack justify="flex-end" spacing={3}>
              <Button variant="ghost" onClick={handleCancel}>
                {t('createOrganization.buttons.cancel')}
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={loading}
                loadingText={
                  isEditMode
                    ? t('createOrganization.buttons.updating')
                    : t('createOrganization.buttons.creating')
                }
                leftIcon={<Icon as={FiUsers} />}
                isDisabled={isEditMode && !organization}
              >
                {isEditMode
                  ? t('createOrganization.buttons.update')
                  : t('createOrganization.buttons.create')}
              </Button>
            </HStack>
          </VStack>
        </form>
      </VStack>
    </Box>
  )
}
